import 'react-flow-renderer/dist/style.css';

import dagre from 'dagre';
import React, {useMemo, useState} from 'react';
import ReactFlow, {
  Background,
  Controls,
  Edge as FlowEdge,
  Handle,
  Node as FlowNode,
  Position,
  ReactFlowProvider,
  useReactFlow,
} from 'react-flow-renderer';

import {parseSpanName} from '../../../pages/wfReactInterface/tsDataModelHooks';
import {TraceViewProps} from './types';
import {getCallDisplayName, getColorForOpName} from './utils';

const NODE_WIDTH = 180;
const NODE_HEIGHT = 40;

type LayoutDirection = 'TB' | 'LR';

// Styles for the flow - updated for react-flow-renderer
const flowStyles = `
  .react-flow__node.selectable.selected {
    border-color: #0066FF;
    box-shadow: 0 0 0 2px #0066FF;
    border-radius: 8px;
  }
  .react-flow__node.selectable:hover {
    box-shadow: 0 0 0 1px #0066FF;
    border-radius: 8px;
  }
`;

interface TraceNodeData {
  label: string;
  color: string;
}

interface TraceNodeProps {
  data: TraceNodeData;
  sourcePosition?: Position;
  targetPosition?: Position;
}

// Custom node component for displaying trace calls
const TraceNode: React.FC<TraceNodeProps> = ({
  data,
  sourcePosition = Position.Bottom,
  targetPosition = Position.Top,
}) => {
  return (
    <div
      style={{
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        background: data.color,
        border: '1px solid #CBD5E1',
        borderRadius: '8px',
        padding: '8px',
      }}>
      <Handle type="target" position={targetPosition} />
      <div className="truncate text-center text-sm">{data.label}</div>
      <Handle type="source" position={sourcePosition} />
    </div>
  );
};

const nodeTypes = {
  traceNode: TraceNode,
};

/**
 * Calculates the layout for nodes and edges using dagre layout engine
 * @param nodes - Array of flow nodes
 * @param edges - Array of flow edges
 * @param direction - Layout direction ('TB' for top-to-bottom, 'LR' for left-to-right)
 * @returns Object containing layouted nodes and edges
 */
const getLayoutedElements = (
  nodes: FlowNode[],
  edges: FlowEdge[],
  direction: LayoutDirection
) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  // Configure the graph layout
  dagreGraph.setGraph({
    rankdir: direction, // Top to bottom or Left to right layout
    nodesep: 50, // Horizontal spacing between nodes
    ranksep: 80, // Vertical spacing between nodes
    edgesep: 30, // Minimum edge separation
    marginx: 20, // Horizontal margin
    marginy: 20, // Vertical margin
  });

  // Add nodes to the graph
  nodes.forEach(node => {
    dagreGraph.setNode(node.id, {width: NODE_WIDTH, height: NODE_HEIGHT});
  });

  // Add edges to the graph
  edges.forEach(edge => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate the layout
  dagre.layout(dagreGraph);

  // Apply the calculated positions to the nodes
  const layoutedNodes = nodes.map(node => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - NODE_HEIGHT / 2,
      },
    };
  });

  return {nodes: layoutedNodes, edges};
};

// Internal flow component that uses the React Flow hooks
const Flow: React.FC<TraceViewProps> = ({
  traceTreeFlat,
  focusedCallId,
  setFocusedCallId,
  setRootCallId,
}) => {
  const [layoutDirection, setLayoutDirection] = useState<LayoutDirection>('LR');
  const {fitView} = useReactFlow();

  // Generate a unique key to force re-render when trace tree changes
  const renderKey = useMemo(() => {
    return Math.random() * Object.keys(traceTreeFlat).length;
  }, [traceTreeFlat]);

  // Calculate nodes and edges from trace tree data
  const {nodes, edges} = useMemo(() => {
    const initialNodes: FlowNode[] = [];
    const initialEdges: FlowEdge[] = [];

    // Determine handle positions based on layout direction
    const sourcePosition =
      layoutDirection === 'TB' ? Position.Bottom : Position.Right;
    const targetPosition =
      layoutDirection === 'TB' ? Position.Top : Position.Left;

    // Create nodes and edges from the trace tree
    Object.entries(traceTreeFlat).forEach(([id, node]) => {
      const call = node.call;
      const opName = parseSpanName(call.op_name);

      initialNodes.push({
        id,
        type: 'traceNode',
        position: {x: 0, y: 0}, // Position will be calculated by dagre
        data: {
          label: getCallDisplayName(call),
          color: getColorForOpName(opName),
        },
        selected: id === focusedCallId,
        sourcePosition,
        targetPosition,
      });

      // Create edges to children
      node.childrenIds.forEach(childId => {
        initialEdges.push({
          id: `${id}-${childId}`,
          source: id,
          target: childId,
          style: {stroke: '#CBD5E1'},
        });
      });
    });

    // Apply the dagre layout
    return getLayoutedElements(initialNodes, initialEdges, layoutDirection);
  }, [focusedCallId, traceTreeFlat, layoutDirection]);

  // Fit view when layout direction changes
  React.useEffect(() => {
    fitView({padding: 0.2});
  }, [fitView, layoutDirection]);

  const toggleLayoutDirection = () => {
    setLayoutDirection(prev => (prev === 'TB' ? 'LR' : 'TB'));
  };

  return (
    <>
      <style>{flowStyles}</style>
      <div style={{position: 'relative', height: '100%'}}>
        {/* Layout Direction Toggle Button */}
        <div
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 10,
            background: 'white',
            border: '1px solid #CBD5E1',
            borderRadius: '4px',
            padding: '4px 8px',
            cursor: 'pointer',
            fontSize: '12px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
          onClick={toggleLayoutDirection}>
          Toggle Direction
        </div>

        <ReactFlow
          key={renderKey}
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodeClick={(event: any, node: any) => setFocusedCallId(node.id)}
          onNodeDoubleClick={(event: any, node: any) => setRootCallId(node.id)}
          fitView
          defaultEdgeOptions={{
            type: 'smoothstep',
            animated: false,
          }}
          elementsSelectable={true}>
          <Background />
          <Controls />
        </ReactFlow>
      </div>
    </>
  );
};

/**
 * GraphView component that displays a trace tree as an interactive graph
 * Uses react-flow-renderer with dagre layout engine for automatic positioning
 */
export const GraphView: React.FC<TraceViewProps> = props => {
  return (
    <div className="h-full overflow-hidden">
      <div className="h-[100%] p-4">
        <ReactFlowProvider>
          <Flow {...props} />
        </ReactFlowProvider>
      </div>
    </div>
  );
};
